import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

// فك حمولة JWT محلياً (بدون تحقق من التوقيع — فقط للعرض بالواجهة، القرار
// الأمني الحقيقي يتم بالسيرفر عند كل طلب كالمعتاد)
function decodeTokenPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch { return null; }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState(() => {
    const payload = decodeTokenPayload(localStorage.getItem('token') || '');
    return payload?.impersonation ? { by: payload.impersonated_by } : null;
  });

  useEffect(() => {
    // ── استقبال توكن انتحال (Impersonate) من لوحة صاحب المنصة ────────────
    // رابط الدخول المباشر يجي بصيغة .../admin?impersonate_token=xxx — نلتقطه
    // هنا مرة واحدة، نحفظه كتوكن الجلسة العادي (بدون refreshToken — جلسة
    // دعم مؤقتة قصيرة الأجل فقط)، وننظّف الرابط من التوكن فوراً حتى لا يبقى
    // بتاريخ المتصفح أو يُنسخ بالغلط لاحقاً.
    const params = new URLSearchParams(window.location.search);
    const impersonateToken = params.get('impersonate_token');
    if (impersonateToken) {
      localStorage.removeItem('refreshToken');
      localStorage.setItem('token', impersonateToken);
      const payload = decodeTokenPayload(impersonateToken);
      if (payload?.impersonation) setImpersonation({ by: payload.impersonated_by });
      params.delete('impersonate_token');
      const cleanUrl = window.location.pathname + (params.toString() ? `?${params}` : '') + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    }

    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then(res => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch((err) => {
          // نخرج فقط عند خطأ مصادقة صريح (401 أو 403)
          // انقطاع الشبكة أو timeout أو أي خطأ آخر → نبقي المستخدم محتفظاً بجلسته
          if (err.response?.status === 401 || err.response?.status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            setUser(null);
          }
          // شبكة منقطعة → لا نحذف شيء، المستخدم يبقى مسجّلاً دخوله
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (phone, password) => {
    const res = await api.post('/auth/login', { phone, password });
    localStorage.setItem('token', res.data.token);
    if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    setImpersonation(null);
    return res.data;
  };

  const register = async (phone, password, confirmPassword) => {
    const res = await api.post('/auth/register', { phone, password, confirmPassword });
    localStorage.setItem('token', res.data.token);
    if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) api.post('/auth/logout', { refreshToken }).catch(()=>{});
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setImpersonation(null);
  };

  const refreshUser = async () => {
    const res = await api.get('/auth/me');
    setUser(res.data);
    localStorage.setItem('user', JSON.stringify(res.data));
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshUser, loading, impersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
